import {
  SimplePropsAdder,
  SimpleUnchangedProps,
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  flowMax,
} from 'ad-hok'
import { getContextHelpers, toObjectKeys } from 'ad-hok-utils'
import { FC } from 'react'
import { useCookies } from 'react-cookie'

import { addLocation, addNavigate } from './addNavigate'
import { getEnvVar } from './env'

const COOKIE_DOMAIN = getEnvVar('COOKIE_DOMAIN')

export type LoginTrigger = 'bookmark' | 'roundup' | 'follow' | 'view_edit'

interface LoginWithRedirectArgs {
  returnTo?: string
  loginTrigger?: LoginTrigger
  showBackButton?: boolean
}

type AddCookies = SimplePropsAdder<{
  cookie: {
    cookies: { [x: string]: any }
    setCookie: (name: string, value: any) => void
    removeCookies: (
      name: string,
      options?: { path: string; domain: string }
    ) => void
  }
}>

export const addCookie: AddCookies = addProps(() => {
  const [cookies, setCookie, removeCookies] = useCookies()

  return {
    cookie: {
      cookies,
      setCookie,
      removeCookies,
    },
  }
})

interface AuthContext {
  isAuthenticated: boolean
  logOut: () => void
  logIn: () => void
  navigateToLogin: (args: LoginWithRedirectArgs) => void
  navigateToRegister: (args: LoginWithRedirectArgs) => void
  invalidateCredentials: () => void
}

export const [addAuthContextProviderInternal, addAuthContext] =
  getContextHelpers<AuthContext>(
    toObjectKeys([
      'isAuthenticated',
      'logOut',
      'logIn',
      'navigateToLogin',
      'navigateToRegister',
      'invalidateCredentials',
    ])
  )

export const AuthProvider: FC = flowMax(
  addDisplayName('AuthProvider'),
  addLocation,
  addCookie,
  addNavigate,
  addState('isAuthenticated', 'setIsAuthenticated', false),
  addHandlers({
    logIn:
      ({ cookie: { setCookie } }) =>
      () => {
        setCookie('trigger_session', 'yes')
      },
    logOut:
      ({ cookie }) =>
      () => {
        cookie.removeCookies('session_id', { path: '/', domain: COOKIE_DOMAIN })
        window.location.reload()
      },
    navigateToLogin:
      ({ navigate, location }) =>
      (args: LoginWithRedirectArgs) => {
        navigate('/login', {
          state: {
            ...location.state,
            showBackButton:
              args.showBackButton ?? location.state?.showBackButton,
            returnTo: args.returnTo ?? window.location.pathname,
            loginTrigger: args.loginTrigger,
          },
        })
      },
    navigateToRegister:
      ({ navigate, location }) =>
      () => {
        navigate('/register', {
          state: { ...location.state, returnTo: window.location.pathname },
        })
      },
    invalidateCredentials:
      ({ cookie }) =>
      () => {
        cookie.removeCookies('session_id', { path: '/', domain: COOKIE_DOMAIN })
      },
  }),
  addEffect(
    ({ cookie, setIsAuthenticated }) =>
      () => {
        setIsAuthenticated(cookie.cookies['session_id'] !== undefined)
      },
    ['cookie.cookies']
  ),
  addAuthContextProviderInternal,
  ({ children }) => <>{children}</>
)

export const authenticate: SimpleUnchangedProps = flowMax(
  addAuthContext,
  addNavigate,
  addLocation,
  addEffect(
    ({ isAuthenticated, navigate, location }) =>
      () => {
        if (isAuthenticated) {
          navigate(location.pathname)
        }
      },
    ['auth.isAuthenticated']
  )
)
